import React from 'react'
import styled from '@emotion/styled'
import { useDispatch, useSelector } from 'react-redux'
import { colors, font, radius, shadow, spacing } from '@mobi/component-library/Theme/Common'
import { Icon } from '@mobi/component-library/Common/V2/Icon'
import { Button } from '@mobi/component-library/Common/V2/Buttons'
import { Input } from '@mobi/component-library/Common/V2/Inputs'
import { setUserPresetDenomination } from '@mobi/betslip/Store/Config'
import { selectPresetDenominations } from '@mobi/betslip/Store/Config/selectors'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import { formatDenominationDisplay } from '@mobi/utils/money/money'

const enum LocalConstants {
  ActiveInputClass = 'js-preset-input--active',
}

export const EditPresetTrayContent: React.FC<{ hideTray: () => void }> = ({ hideTray }) => {
  const dispatch = useDispatch()
  const presetDenominations = useSelector(selectPresetDenominations)

  const initialInputState = presetDenominations.map((value, index) => ({
    index,
    value: value.user || value.default,
  }))

  const [inputTypeValues, setInputTypeValues] = React.useState(initialInputState)
  const [activeInput, setActiveInput] = React.useState<number | null>(null)

  const submitBetValues = () => {
    // Convert values below $0.5 to $0.5 when saving
    const correctedValues = inputTypeValues.map(input => ({
      ...input,
      value: input.value > 0 && input.value < 0.5 ? 0.5 : input.value,
    }))

    dispatch(setUserPresetDenomination(correctedValues))
    hideTray()
    addToast({
      id: 'BetSlip keyboard updated',
      isDismissable: true,
      title: 'BetSlip keyboard updated',
      icon: 'success',
    })
  }

  const handleInputChange = (idx: number, value: number) => {
    setInputTypeValues(prevValues => {
      const updatedValues = [...prevValues]
      updatedValues[idx].value = value
      return updatedValues
    })
  }

  const isSaveActive = inputTypeValues?.every((input, index) => {
    const currentValue = input.value
    const originalValue = presetDenominations[index].user

    // Check if value is unchanged (accounting for auto-correction on save)
    const isUnchanged =
      currentValue === null ||
      currentValue === 0 ||
      originalValue === currentValue ||
      (currentValue === 0.5 && originalValue === currentValue) ||
      (currentValue === 0.5 && originalValue !== null && originalValue < 0.5 && originalValue > 0)

    return isUnchanged
  })

  return (
    <WrapperStyled>
      <div>
        <p>Save up to 3 custom amounts for one tap selection when placing a bet</p>
        <div>
          {inputTypeValues?.map((config, idx) => (
            <Input
              key={idx}
              prefix='$'
              name={`button ${idx + 1}`}
              label={`Button ${idx + 1}`}
              type='number'
              value={'' + config.value}
              onMouseDown={() => setActiveInput(config.index + 1)}
              onMouseLeave={() => {
                if (config.value === 0) {
                  handleInputChange(idx, presetDenominations?.[idx]?.default)
                }
                setActiveInput(null)
              }}
              onChange={e => {
                const value = parseFloat(e.target.value)
                const processedValue = isNaN(value)
                  ? 0
                  : Math.max(0, Math.min(10000, Math.round(value * 100) / 100))
                handleInputChange(idx, processedValue)
              }}
            />
          ))}
        </div>
      </div>
      <KeyPadStyled>
        <button disabled type='button'>
          1
        </button>
        <button disabled type='button'>
          2
        </button>
        <button disabled type='button'>
          3
        </button>
        <button disabled type='button'>
          4
        </button>
        <button disabled type='button'>
          5
        </button>
        <button disabled type='button'>
          6
        </button>
        <button disabled type='button'>
          7
        </button>
        <button disabled type='button'>
          8
        </button>
        <button disabled type='button'>
          9
        </button>
        <button disabled type='button'>
          .
        </button>
        <button disabled type='button'>
          0
        </button>
        <button disabled type='button'>
          <Icon name='DuocolorDelete' size='1.6rem' color='inherit' />
        </button>

        <div>
          <button disabled type='button'>
            Clear
          </button>

          {inputTypeValues?.map((input, idx) => (
            <button
              key={idx}
              type='button'
              className={activeInput === idx + 1 ? LocalConstants.ActiveInputClass : ''}
            >
              +{' '}
              {formatDenominationDisplay(
                input.value === 0 ? presetDenominations[idx].default : input.value
              )}
            </button>
          ))}
        </div>
      </KeyPadStyled>

      <Button disabled={isSaveActive} onClick={submitBetValues} isFullWidth={true}>
        Save
      </Button>
    </WrapperStyled>
  )
}

const WrapperStyled = styled.div({
  marginBottom: spacing.md,

  '> div:first-child': {
    display: 'flex',
    flexDirection: 'column',
    fontFamily: font.family.primary,
    gap: spacing.smx2,
    marginBottom: spacing.smx1,
    fontSize: font.size.md.fontSize,
    lineHeight: font.size.md.lineHeight,

    'div, label': {
      fontSize: font.size.md.fontSize,
      lineHeight: font.size.md.lineHeight,
    },

    '> p': {
      color: colors.neutral[800],
      fontSize: font.size.md.fontSize,
      fontWeight: font.weight.medium,
      textAlign: 'left',
      marginTop: 0,
    },

    '> div:first-of-type': {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      columnGap: spacing.smx2,
    },
  },
})

const KeyPadStyled = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gridTemplateRows: 'repeat(4, auto)',
  gap: spacing.smx2,
  padding: spacing.smx2,
  borderRadius: radius.lg,
  backgroundColor: colors.studio[50],

  '> div:first-of-type': {
    gridColumnStart: '4',
    gridColumnEnd: '5',
    gridRowStart: '1',
    gridRowEnd: '5',
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.smx2,
  },

  button: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '4.5rem',
    fontFamily: font.family.primary,
    fontSize: font.size.xl.fontSize,
    letterSpacing: font.size.xl.letterSpacing,
    lineHeight: font.size.xl.lineHeight,
    fontWeight: font.weight.medium,
    borderRadius: radius.md,
    border: '0.1rem solid ' + colors.studio[50],
    backgroundColor: colors.white,
    color: colors.lavender[900],
    boxShadow: shadow.sm,
    userSelect: 'none',
    pointerEvents: 'none',

    [`&.${LocalConstants.ActiveInputClass}`]: {
      backgroundColor: colors.studio[500],
      color: colors.white,
    },

    ':disabled': {
      color: colors.neutral[500],
    },
  },
})
